import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  CalendarDays,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  FileSearch,
  Gavel,
  Landmark,
  LoaderCircle,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
} from "lucide-react";
import { AppLink, FeedbackBanner, PageHeader, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";
import { catalogLinkState } from "../components/LegalCatalogUi.jsx";
import {
  buildJurisprudenceSearchUrl,
  JURISPRUDENCE_TRIBUNALS,
  jurisprudenceSearchFromUrl,
} from "../lib/jurisprudence-search-query.js";

const exampleQueries = [
  "Valoración de la pericia médica en accidentes laborales",
  "Cuantificación del daño moral contractual",
  "Rechazo de cobertura por denuncia tardía del siniestro",
];

const matchLabels = {
  exact: "Coincidencia exacta",
  hybrid: "Coincidencia híbrida",
  semantic: "Coincidencia conceptual",
  lexical: "Coincidencia textual",
};

function formatDate(value, options = { dateStyle: "medium" }) {
  if (!value) return "Fecha no informada";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "Fecha no informada";
  return new Intl.DateTimeFormat("es-AR", options).format(date);
}

function coverageLabel(coverage) {
  if (!coverage?.fromDate || !coverage?.toDate) return "Cobertura en actualización";
  const options = { month: "short", year: "numeric" };
  return `${formatDate(coverage.fromDate, options)}–${formatDate(coverage.toDate, options)}`;
}

function openJustiniano(prompt) {
  window.dispatchEvent(new CustomEvent("justiniano:compose", {
    detail: { appId: "instrumenta", prompt },
  }));
}

export function JurisprudenceSearchPage() {
  const api = useApi();
  const { path, search: routeSearch, navigate } = useRouter();
  const route = useMemo(
    () => jurisprudenceSearchFromUrl(routeSearch),
    [routeSearch],
  );
  const [form, setForm] = useState(route);
  const [coverage, setCoverage] = useState(null);
  const [results, setResults] = useState([]);
  const [searchMode, setSearchMode] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [copiedId, setCopiedId] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;
    api.getJurisprudenceCoverage()
      .then((data) => active && setCoverage(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    let active = true;
    setForm(route);
    setSelected([]);
    setError("");
    setWarning("");
    if (!route.query) {
      setResults([]);
      setSearchMode("");
      setSearched(false);
      setLoading(false);
      return () => {
        active = false;
      };
    }
    if (route.query.length < 3) {
      setResults([]);
      setSearched(true);
      setError("Describí el criterio o problema jurídico con al menos 3 caracteres.");
      return () => {
        active = false;
      };
    }
    setLoading(true);
    setSearched(false);
    api.searchJurisprudence({
      query: route.query,
      tribunal: route.tribunal,
      fromDate: route.fromDate,
      toDate: route.toDate,
    }).then((data) => {
      if (!active) return;
      setResults(data.items || []);
      setCoverage((current) => data.coverage || current);
      setSearchMode(data.searchMode || "lexical");
      setWarning(data.warning || "");
      setSearched(true);
    }).catch((requestError) => {
      if (!active) return;
      setResults([]);
      setSearched(true);
      setError(requestError.message);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [
    api,
    reloadToken,
    route.query,
    route.tribunal,
    route.fromDate,
    route.toDate,
  ]);

  const submitSearch = (event) => {
    event.preventDefault();
    const query = form.query.trim();
    if (query.length < 3) {
      setError("Describí el criterio o problema jurídico con al menos 3 caracteres.");
      return;
    }
    if (form.fromDate && form.toDate && form.fromDate > form.toDate) {
      setError("La fecha desde no puede ser posterior a la fecha hasta.");
      return;
    }
    const target = buildJurisprudenceSearchUrl({ ...form, query });
    if (`${path}${routeSearch}` === target) {
      setReloadToken((current) => current + 1);
      return;
    }
    navigate(target);
  };

  const runExample = (query) => {
    setForm((current) => ({ ...current, query }));
    navigate(buildJurisprudenceSearchUrl({ ...form, query }));
  };

  const toggleSelected = (id) => {
    setSelected((current) => current.includes(id)
      ? current.filter((item) => item !== id)
      : [...current, id]);
  };

  const copyCitation = async (result) => {
    try {
      await navigator.clipboard.writeText(result.citation);
      setCopiedId(result.id);
      window.setTimeout(() => setCopiedId(""), 1800);
    } catch {
      setError("No pudimos copiar la cita. Seleccioná el texto e intentá nuevamente.");
    }
  };

  const compareSelected = () => {
    const decisions = results.filter(({ id }) => selected.includes(id));
    if (!decisions.length) return;
    openJustiniano([
      "Compará los siguientes fallos. Explicá el problema jurídico, el criterio de cada tribunal, sus coincidencias y diferencias, y citá los pasajes que respalden la comparación:",
      "",
      ...decisions.map((result) => `- ${result.citation}`),
    ].join("\n"));
  };

  return (
    <div className="page jurisprudence-search-page">
      <PageHeader
        hero
        eyebrow="Instrumenta · Fuentes oficiales"
        title="Buscar jurisprudencia"
        description="Buscá por criterio jurídico, situación fáctica, carátula o CUIJ. Combinamos coincidencias textuales con búsqueda semántica sobre el contenido de cada sentencia."
      />

      <section className="jurisprudence-coverage" aria-label="Cobertura de la base">
        <span><ShieldCheck size={15} /> Fuente oficial</span>
        <span><Landmark size={15} /> Poder Judicial de Mendoza</span>
        <span><CalendarDays size={15} /> {coverageLabel(coverage)}</span>
        {coverage?.total ? (
          <strong>{coverage.total.toLocaleString("es-AR")} sentencias</strong>
        ) : null}
      </section>

      <form className="jurisprudence-search-form" onSubmit={submitSearch}>
        <label htmlFor="jurisprudence-query">
          <span>¿Qué criterio, problema o situación querés encontrar?</span>
          <span className="jurisprudence-query-control">
            <Search size={22} />
            <textarea
              id="jurisprudence-query"
              value={form.query}
              rows={2}
              onChange={(event) => {
                setForm((current) => ({ ...current, query: event.target.value }));
                setError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Ej. rechazo de pericia médica por falta de fundamentación en un accidente laboral"
              autoFocus
            />
            <button className="primary-button" disabled={loading || !form.query.trim()}>
              {loading ? <LoaderCircle className="spin" size={17} /> : <Sparkles size={17} />}
              {loading ? "Buscando…" : "Buscar"}
            </button>
          </span>
        </label>

        <div className="jurisprudence-filters">
          <span className="jurisprudence-filter-title">
            <SlidersHorizontal size={16} /> Afinar búsqueda
          </span>
          <label>
            Tribunal
            <select
              value={form.tribunal}
              onChange={(event) => setForm((current) => ({
                ...current,
                tribunal: event.target.value,
              }))}
            >
              {JURISPRUDENCE_TRIBUNALS.map((option) => (
                <option value={option.value} key={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            Desde
            <input
              type="date"
              value={form.fromDate}
              max={form.toDate || undefined}
              onChange={(event) => setForm((current) => ({
                ...current,
                fromDate: event.target.value,
              }))}
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              value={form.toDate}
              min={form.fromDate || undefined}
              onChange={(event) => setForm((current) => ({
                ...current,
                toDate: event.target.value,
              }))}
            />
          </label>
        </div>
      </form>

      {error && <FeedbackBanner>{error}</FeedbackBanner>}
      {warning && <FeedbackBanner>{warning}</FeedbackBanner>}

      {!searched && !loading && !route.query && (
        <section className="jurisprudence-start-state">
          <div>
            <span className="jurisprudence-start-icon"><FileSearch size={24} /></span>
            <div>
              <h2>Buscá ideas, no sólo palabras</h2>
              <p>Describí el problema como lo harías con un colega. También podés ingresar una carátula, CUIJ o frase exacta.</p>
            </div>
          </div>
          <div className="jurisprudence-examples" aria-label="Búsquedas de ejemplo">
            {exampleQueries.map((example) => (
              <button type="button" onClick={() => runExample(example)} key={example}>
                <Search size={14} /> {example}
              </button>
            ))}
          </div>
        </section>
      )}

      {loading && (
        <section className="jurisprudence-loading" aria-live="polite">
          <LoaderCircle className="spin" size={22} />
          <span>
            <strong>Buscando en el texto de las sentencias…</strong>
            <small>Combinamos términos, conceptos y metadatos del tribunal.</small>
          </span>
        </section>
      )}

      {searched && !loading && results.length > 0 && (
        <section className="jurisprudence-results" aria-live="polite">
          <header>
            <div>
              <span className="eyebrow">Resultados relevantes</span>
              <h2>{results.length} sentencia{results.length === 1 ? "" : "s"} para revisar</h2>
              <p>Ordenadas por relevancia. Verificá siempre el criterio en la fuente original.</p>
            </div>
            <span className={`search-mode-pill is-${searchMode}`}>
              {searchMode === "hybrid" ? <Sparkles size={14} /> : <Search size={14} />}
              {searchMode === "hybrid" ? "Búsqueda híbrida" : "Búsqueda textual"}
            </span>
          </header>

          <div className="jurisprudence-result-list">
            {results.map((result, index) => {
              const isSelected = selected.includes(result.id);
              return (
                <article className={`jurisprudence-result-card${isSelected ? " is-selected" : ""}`} key={result.id}>
                  <button
                    type="button"
                    className="jurisprudence-select"
                    aria-label={isSelected ? "Quitar de la comparación" : "Seleccionar para comparar"}
                    aria-pressed={isSelected}
                    onClick={() => toggleSelected(result.id)}
                  >
                    {isSelected ? <Check size={15} /> : <span>{index + 1}</span>}
                  </button>
                  <div className="jurisprudence-result-content">
                    <div className="jurisprudence-result-kicker">
                      <span className={`match-pill is-${result.matchType}`}>
                        {result.matchType === "hybrid" || result.matchType === "semantic"
                          ? <Sparkles size={12} />
                          : <Search size={12} />}
                        {matchLabels[result.matchType]}
                      </span>
                      {result.official && <span className="official-source"><CheckCircle2 size={12} /> Oficial</span>}
                    </div>
                    <h3>
                      <AppLink
                        to={result.url}
                        state={catalogLinkState(`${path}${routeSearch}`)}
                      >
                        {result.caseName}
                      </AppLink>
                    </h3>
                    <div className="jurisprudence-result-meta">
                      <span><Landmark size={14} /> {result.court}</span>
                      <span><CalendarDays size={14} /> {formatDate(result.date)}</span>
                      {result.cuij && <span><Gavel size={14} /> {result.cuij}</span>}
                    </div>
                    <div className="jurisprudence-passage">
                      <span>Pasaje relevante</span>
                      <blockquote>{result.passage}</blockquote>
                      <small><Sparkles size={12} /> {result.matchReason}</small>
                    </div>
                    <div className="jurisprudence-result-actions">
                      <AppLink
                        className="primary-button"
                        to={result.url}
                        state={catalogLinkState(`${path}${routeSearch}`)}
                      >
                        <FileSearch size={15} /> Abrir fuente Tribunalea
                      </AppLink>
                      {result.sourceUrl && (
                        <a className="secondary-button" href={result.sourceUrl} target="_blank" rel="noreferrer">
                          <ExternalLink size={15} /> Fuente original externa
                        </a>
                      )}
                      <button className="secondary-button" type="button" onClick={() => copyCitation(result)}>
                        {copiedId === result.id ? <Check size={15} /> : <Copy size={15} />}
                        {copiedId === result.id ? "Cita copiada" : "Copiar cita"}
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => openJustiniano(
                          `Analizá este fallo e identificá hechos relevantes, problema jurídico, criterio, fundamentos y decisión. Citá los pasajes utilizados: ${result.citation}`,
                        )}
                      >
                        <Bot size={15} /> Analizar con Justiniano
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {searched && !loading && !error && results.length === 0 && (
        <section className="jurisprudence-empty-state">
          <Search size={25} />
          <h2>No encontramos coincidencias</h2>
          <p>Probá con una descripción más general, menos términos o todos los tribunales.</p>
        </section>
      )}

      {selected.length > 0 && (
        <aside className="jurisprudence-selection-bar" aria-live="polite">
          <span><CheckCircle2 size={18} /> {selected.length} fallo{selected.length === 1 ? "" : "s"} seleccionado{selected.length === 1 ? "" : "s"}</span>
          <button className="primary-button" type="button" onClick={compareSelected}>
            <Bot size={16} /> Comparar con Justiniano
          </button>
        </aside>
      )}
    </div>
  );
}
